// A board is data, not hard-coded geometry.
//
// v1 and v3 ship one fixed board, so it used to live as module constants evaluated at import.
// The lab needs to try *other* boards — different pin fields, different bin counts, special pins
// on or off in the main game — and judge how they play. That means the geometry has to be
// swappable at runtime while the PHYSICS stays byte-identical: the same engine, the same
// collision code, the same seeds. Only the board it runs on changes.
//
// DEFAULT_BOARD reproduces the shipped values exactly, and nothing reads a spec until someone
// calls setBoard(), so v1 and v3 are untouched.
import {
  CANVAS_WIDTH, PIN_START,
  PIN_SPACING_X_BASE, PIN_SPACING_Y_BASE,
  SPECIAL_PIN_SPACING_X_BASE, SPECIAL_PIN_SPACING_Y_BASE,
  PIN_RADIUS_BASE, SPECIAL_PIN_RADIUS_BASE, BALL_RADIUS_BASE,
} from './constants';

export type Pin = { x: number; y: number; isSpecial?: boolean; visible: boolean };

export type BoardPhysics = {
  /** Bounce energy kept on a normal pin. Lower = the ball settles faster and drifts less. */
  normalRestitution: number;
  /** Bounce on a special pin (a "bumper"). 1.0 in v1/v3 — a lossless kick. */
  specialRestitution: number;
  /** Extra outward speed a special pin adds. */
  specialBoost: number;
  /**
   * Minimum speed AWAY from a bumper after contact, whatever the approach angle.
   *
   * Reflection alone scales with vDotN, so a glancing touch barely deflects: measured on the v4
   * board, the median bumper hit changed the ball's speed by 1.05x — invisible. The existing
   * minimum-speed clamp did not help because it bounds total speed, not outward speed, and a ball
   * skimming past a bumper already has plenty of total speed. This floors the outward component so
   * every contact reads as a kick.
   */
  specialKick: number;
  /** Sideways speed kept after a glancing hit. Higher = wider spread. */
  tangentialDamping: number;
  /** Initial sideways velocity range at the drop point. */
  initialVelocity: number;
  /** Downward acceleration. A taller board needs more of it to keep flights from dragging. */
  gravity: number;
  /**
   * Pin, bumper and ball radii.
   *
   * These are physics, not decoration: what actually scatters a ball is the radius RELATIVE to the
   * pin pitch. v1 sits at 8/63 = 0.127. Shrinking the pitch for a wider board while leaving the
   * radius at 8 quietly makes the obstacles half again as large in proportion, which both crowds
   * the picture and widens the landing spread. Scaling them together keeps a bigger board playing
   * — and looking — like the original one.
   */
  pinRadius: number;
  specialPinRadius: number;
  ballRadius: number;
};

export const DEFAULT_PHYSICS: BoardPhysics = {
  normalRestitution: 0.5,
  specialRestitution: 1.0,
  specialBoost: 1.5,
  specialKick: 0,          // v1/v3 keep the original behaviour
  tangentialDamping: 0.75,
  initialVelocity: 2.0,
  gravity: 0.3,
  pinRadius: PIN_RADIUS_BASE,
  specialPinRadius: SPECIAL_PIN_RADIUS_BASE,
  ballRadius: BALL_RADIUS_BASE,
};

export type BoardSpec = {
  /** Rows of 0/1. 1 = a pin. Rows are centred horizontally, so ragged rows are fine. */
  pinLayout: readonly (readonly number[])[];
  /** Special ("bumper") pins — higher restitution and a speed boost. */
  specialPinLayout: readonly (readonly number[])[];
  /** Solid sides following the silhouette. Off by default so v1/v3 are unaffected. */
  walls?: boolean;
  specialPinConfig: { offsetX: number; offsetY: number; startRow: number };
  /** Payout multiplier per bin. Its LENGTH sets the bin count and the landing boundaries. */
  binMultipliers: readonly number[];
  /**
   * Whether special pins exist during the MAIN game. Turning this off keeps them for the bonus
   * round only — the ball falls through a plain field until a bonus starts.
   */
  specialPinsInMain: boolean;
  /** Label, for the lab UI. */
  name: string;

  // ---- optional: default to the shipped values so existing boards keep working ----
  /** Column pitch. Also the bin pitch, so it sets how many bins fit across the canvas. */
  pinSpacingX?: number;
  pinSpacingY?: number;
  specialSpacingX?: number;
  specialSpacingY?: number;
  physics?: Partial<BoardPhysics>;
  /**
   * Bumpers placed as [row, col] on the board's OWN pin lattice, REPLACING the pin there.
   *
   * The older specialPinLayout/specialPinConfig pair carried its own spacing constants and added
   * bumpers on top of the existing field. On any board whose pitch differs from those constants
   * that puts a 16px bumper inside an 8px pin — measured at 2px separation on the 58x44 board —
   * so the ball collides with the pin and never reaches the bumper's surface at all. Swapping a
   * lattice cell guarantees the bumper is the only thing at that point, and that a ball can
   * actually reach it.
   */
  bumperCells?: readonly (readonly [number, number])[];
  /**
   * Cells to CLEAR, as [row, col] on the pin lattice. Lets one board open a chamber the other does
   * not have: the main game runs on a solid field, and entering the bonus visibly empties the
   * middle of the board to expose the bumpers inside it.
   */
  voidCells?: readonly (readonly [number, number])[];
  /**
   * Bin multipliers used during the BONUS round. The board geometry is identical — only what the
   * slots pay changes — so a bonus can be dramatic without becoming a different game.
   */
  bonusBinMultipliers?: readonly number[];
};

/** Exactly what v1/v3 ship today. Changing anything here changes the live game. */
export const DEFAULT_BOARD: BoardSpec = {
  name: 'shipped',
  pinLayout: [
    [1, 1, 1, 1, 1],
    [1, 1, 1, 1],
    [1, 1, 0, 1, 1],
    [1, 1, 0, 0, 1, 1],
    [1, 1, 0, 0, 0, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1],
    [1, 1, 1, 1, 1, 1, 1, 1, 1],   // hidden row
  ],
  specialPinLayout: [[1, 1], [1]],
  specialPinConfig: { offsetX: 0, offsetY: 0, startRow: 3 },
  binMultipliers: [1.65, 1.05, 0.45, 0.33, 0.29, 0.29, 0.33, 0.45, 1.05, 1.65],
  specialPinsInMain: true,
};

export type BuiltBoard = {
  spec: BoardSpec;
  physics: BoardPhysics;
  spacingX: number;
  spacingY: number;
  /** y below which a ball is considered landed. Scales with the row count. */
  bottomY: number;
  pins: Pin[];
  /** Pins minus the special ones — used when specialPinsInMain is false. */
  mainPins: Pin[];
  layoutDimensions: { rowCount: number; maxCols: number };
  binCount: number;
  binWidth: number;
  leftmostBinX: number;
  /**
   * Side walls, as [leftX, rightX] sampled per pin row and linearly interpolated between them, so
   * the barrier follows the silhouette instead of being a straight vertical line. null when the
   * board has none, which is what v1 and v3 use.
   */
  walls: { ys: number[]; left: number[]; right: number[] } | null;
};

/** Geometry only. Identical arithmetic to the original initializePins(). */
export function buildBoard(spec: BoardSpec): BuiltBoard {
  const spacingX = spec.pinSpacingX ?? PIN_SPACING_X_BASE;
  const spacingY = spec.pinSpacingY ?? PIN_SPACING_Y_BASE;
  const spSpacingX = spec.specialSpacingX ?? SPECIAL_PIN_SPACING_X_BASE;
  const spSpacingY = spec.specialSpacingY ?? SPECIAL_PIN_SPACING_Y_BASE;
  const physics = { ...DEFAULT_PHYSICS, ...(spec.physics ?? {}) };
  const layout = spec.pinLayout;
  const layoutDimensions = {
    rowCount: layout.length,
    maxCols: layout.length > 0 ? layout[0].length : 0,
  };

  const pins: Pin[] = [];
  const centerX = CANVAS_WIDTH / 2;
  const colSpacing = spacingX;

  for (let rowIndex = 0; rowIndex < layoutDimensions.rowCount; rowIndex++) {
    const rowData = layout[rowIndex];
    if (!rowData) continue;

    const y = (rowIndex + 1) * spacingY + PIN_START;
    const rowStartX = centerX - ((rowData.length - 1) / 2) * colSpacing;
    const isLastRow = rowIndex === layoutDimensions.rowCount - 1;

    for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
      if (rowData[colIndex] === 1) {
        const x = rowStartX + colIndex * colSpacing;
        // the final row is the invisible floor guide the original board also used
        if (isLastRow) pins.push({ x, y: y - 5, visible: false });
        else pins.push({ x, y, visible: true });
      }
    }
  }

  // clear cells first, so a bumper can then be dropped into the space that opens
  if (spec.voidCells?.length) {
    for (const [row, col] of spec.voidCells) {
      const rowData = layout[row];
      if (!rowData) continue;
      const rowStartX = centerX - ((rowData.length - 1) / 2) * colSpacing;
      const x = rowStartX + col * colSpacing;
      const y = (row + 1) * spacingY + PIN_START;
      const hit = pins.findIndex((p) => Math.abs(p.x - x) < 0.5 && Math.abs(p.y - y) < 0.5);
      if (hit >= 0) pins.splice(hit, 1);
    }
  }

  // lattice-cell bumpers: swap the pin out so the bumper is reachable
  if (spec.bumperCells?.length) {
    for (const [row, col] of spec.bumperCells) {
      const rowData = layout[row];
      if (!rowData) continue;
      const rowStartX = centerX - ((rowData.length - 1) / 2) * colSpacing;
      const x = rowStartX + col * colSpacing;
      const y = (row + 1) * spacingY + PIN_START;
      const hit = pins.findIndex((p) => Math.abs(p.x - x) < 0.5 && Math.abs(p.y - y) < 0.5);
      if (hit >= 0) pins[hit] = { x, y, isSpecial: true, visible: true };
      else pins.push({ x, y, isSpecial: true, visible: true });
    }
  }

  const specialColSpacing = spSpacingX;
  const specialCenterX = centerX + spec.specialPinConfig.offsetX * spacingX;
  const specialStartY =
    (spec.specialPinConfig.startRow + 1) * spacingY + PIN_START +
    spec.specialPinConfig.offsetY * spacingY;

  for (let rowIndex = 0; rowIndex < spec.specialPinLayout.length; rowIndex++) {
    const rowData = spec.specialPinLayout[rowIndex];
    const actualRowIndex = spec.specialPinConfig.startRow + rowIndex;
    if (actualRowIndex >= layoutDimensions.rowCount) continue;

    const y = specialStartY + rowIndex * spSpacingY;
    const rowStartX = specialCenterX - ((rowData.length - 1) / 2) * specialColSpacing;
    for (let colIndex = 0; colIndex < rowData.length; colIndex++) {
      if (rowData[colIndex] === 1) {
        pins.push({ x: rowStartX + colIndex * specialColSpacing, y, isSpecial: true, visible: true });
      }
    }
  }

  /**
   * The reference board is drawn with thick solid sides, and without them a bumper kick simply
   * throws the ball out of play: measured over the full seed space, 17.4% of bonus balls left the
   * board and had to be re-seeded. Walls keep the kick strong — which is what the round is for —
   * without spending one ball in six on a silent retry.
   */
  let walls: BuiltBoard['walls'] = null;
  if (spec.walls) {
    const byRow = new Map<number, { min: number; max: number }>();
    for (let rowIndex = 0; rowIndex < layoutDimensions.rowCount; rowIndex++) {
      const rowData = layout[rowIndex];
      if (!rowData || !rowData.some((v) => v === 1)) continue;
      // width comes from the LAYOUT, not from the built pins: a bonus board with its chamber
      // cleared must keep the same outline as the main board it mirrors.
      const rowStartX = centerX - ((rowData.length - 1) / 2) * colSpacing;
      byRow.set(rowIndex, { min: rowStartX, max: rowStartX + (rowData.length - 1) * colSpacing });
    }
    const ordered = [...byRow.entries()].sort((a, b) => a[0] - b[0]);
    const half = colSpacing / 2;
    const halfWidth = ordered.map(([, e]) => (e.max - e.min) / 2 + half);
    /**
     * Widen-only, all the way down: the wall never narrows.
     *
     * Two reasons, and they agree. Physically, a staggered field alternates n and n-1 pins per row,
     * so taking each row's own extent puts a NOTCH in the wall wherever a short row sits between two
     * long ones — a ball riding that notch gets pinched between the sloping wall and the pin below
     * it and never descends. Traced, one such ball cycled through the same six frames until the
     * 10,000-iteration limit, and 0.12% of all bonus balls did it. Visually, the reference board is
     * drawn with sides that run straight down past the bins to the base; it has no inward kink at
     * the bottom, and the last row being one pin narrower is a property of the lattice, not of the
     * cabinet.
     */
    let run = 0;
    for (let i = 0; i < halfWidth.length; i++) { run = Math.max(run, halfWidth[i]); halfWidth[i] = run; }
    const ys: number[] = [], left: number[] = [], right: number[] = [];
    ordered.forEach(([rowIndex], i) => {
      ys.push((rowIndex + 1) * spacingY + PIN_START);
      left.push(centerX - halfWidth[i]);
      right.push(centerX + halfWidth[i]);
    });
    walls = { ys, left, right };
  }

  const binCount = spec.binMultipliers.length;
  const binWidth = spacingX;
  return {
    spec,
    physics,
    spacingX,
    spacingY,
    bottomY: layoutDimensions.rowCount * spacingY + PIN_START,
    pins,
    mainPins: pins.filter((p) => !p.isSpecial),
    layoutDimensions,
    binCount,
    binWidth,
    walls,
    leftmostBinX: CANVAS_WIDTH / 2 - ((binCount - 1) / 2) * binWidth,
  };
}

// ---------------------------------------------------------------------------
// Active board. Engines read this at construction, so swapping it takes effect on the next
// engine — which is what the lab does when you edit the board.
// ---------------------------------------------------------------------------
let active: BuiltBoard = buildBoard(DEFAULT_BOARD);

export const getBoard = (): BuiltBoard => active;
export const isDefaultBoard = () => active.spec === DEFAULT_BOARD;

/** Pass null to restore the shipped board. */
export function setBoard(spec: BoardSpec | null): BuiltBoard {
  active = buildBoard(spec ?? DEFAULT_BOARD);
  return active;
}
